import React, { useRef } from 'react';
import { getPosterUrl } from '../services/api';
import { trackEvent } from '../services/analytics';

const SERIF = "'Newsreader', Georgia, serif";

/**
 * Horizontal poster rail — building block for the editorial browse section
 * (Top 10, New, genre rows). Warm near-black film-magazine theme.
 *
 * Props: title, items (MovieResult[]), rank (gold 1..N numerals), onItemClick, onPlayTrailer
 */
const ContentRail = ({ title, items = [], rank = false, onItemClick, onPlayTrailer }) => {
  const scrollerRef = useRef(null);
  if (!items.length) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' });
  };

  const titleFor = (m) => m.title || m.name || '';
  const yearFor = (m) => {
    const d = m.release_date || m.first_air_date || '';
    return d ? String(d).slice(0, 4) : '';
  };

  const handleClick = (m) => {
    trackEvent('Rail Item Clicked', { rail: title, id: m.id, title: titleFor(m) });
    onItemClick && onItemClick(m);
  };

  const handlePlay = (e, m) => {
    e.stopPropagation();
    const key = m.trailer_key;
    if (!key) return;
    trackEvent('Trailer Played', { rail: title, id: m.id, title: titleFor(m), source: 'rail' });
    if (onPlayTrailer) onPlayTrailer(key);
    else window.open(`https://www.youtube.com/watch?v=${key}`, '_blank');
  };

  return (
    <section className="mb-10 group/rail">
      <div className="flex items-end justify-between mb-4">
        <h3 style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, color: '#F1ECE2', letterSpacing: '-0.01em' }}>
          {title}
        </h3>
        <div className="hidden sm:flex gap-2 opacity-0 group-hover/rail:opacity-100 transition-opacity">
          <button onClick={() => scrollBy(-1)} aria-label="Scroll left"
            className="w-8 h-8 rounded-full flex items-center justify-center hover:text-binge-ink transition-colors"
            style={{ border: '1px solid rgba(241,236,226,0.2)', color: '#C9C2B4' }}>‹</button>
          <button onClick={() => scrollBy(1)} aria-label="Scroll right"
            className="w-8 h-8 rounded-full flex items-center justify-center hover:text-binge-ink transition-colors"
            style={{ border: '1px solid rgba(241,236,226,0.2)', color: '#C9C2B4' }}>›</button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto pb-2 snap-x scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((m, i) => {
          const poster = getPosterUrl(m.poster_path, 'w342');
          return (
            <button
              key={m.id ?? i}
              onClick={() => handleClick(m)}
              className={`group/tile relative flex-shrink-0 snap-start text-left ${rank ? 'pl-8 sm:pl-11' : ''}`}
            >
              {rank && (
                <span
                  className="absolute left-0 bottom-7 leading-none select-none"
                  style={{ fontFamily: SERIF, fontSize: '5.5rem', fontWeight: 600, color: '#DCA85E', opacity: 0.9 }}
                >
                  {i + 1}
                </span>
              )}
              <div
                className="relative w-36 sm:w-40 aspect-[2/3] rounded overflow-hidden transition-transform duration-300 hover:-translate-y-1"
                style={{ background: 'rgba(241,236,226,0.05)', boxShadow: 'inset 0 0 0 1px rgba(241,236,226,0.12)' }}
              >
                {poster ? (
                  <img src={poster} alt={titleFor(m)} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs px-2 text-center" style={{ color: '#8F887C' }}>{titleFor(m)}</div>
                )}
                {m.trailer_key && (
                  <span
                    role="button"
                    aria-label="Watch trailer"
                    onClick={(e) => handlePlay(e, m)}
                    className="absolute inset-0 m-auto w-11 h-11 rounded-full flex items-center justify-center opacity-0 group-hover/tile:opacity-100 transition-opacity"
                    style={{ background: 'rgba(20,17,20,0.7)', color: '#F1ECE2', backdropFilter: 'blur(2px)' }}
                  >
                    <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                )}
              </div>
              <div className="w-36 sm:w-40 mt-2.5">
                <p className="line-clamp-1" style={{ fontSize: 13.5, color: '#E5DFD2' }}>{titleFor(m)}</p>
                <p style={{ fontSize: 12, color: '#8F887C', marginTop: 2 }}>
                  {m.vote_average > 0 && <span style={{ color: '#DCA85E' }}>★ {Number(m.vote_average).toFixed(1)}</span>}
                  {m.vote_average > 0 && yearFor(m) && <span> · </span>}
                  {yearFor(m)}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ContentRail;
